import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * The filter bar every report shares (gitaumoses4/chukua#200): a period (`from`/`to`) or an as-of date, and the
 * truck, driver, customer and route a figure may be narrowed to. A report ignores a filter it does not declare
 * (`ReportSummary.filters`) rather than refusing it, so one URL can move between reports.
 */
const reportFilters = {
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  asOf: z.iso.date().optional(),
  customerId: listFilter(z.uuid()),
  truckId: listFilter(z.uuid()),
  driverId: listFilter(z.uuid()),
  originZone: listFilter(z.string().trim().min(1).max(50)),
  destinationZone: listFilter(z.string().trim().min(1).max(50)),
  currency: listFilter(z.enum(CURRENCIES)),
};

function hasOrderedPeriod({ from, to }: { from?: string; to?: string }): boolean {
  return !from || !to || from <= to;
}
const periodIssue = { message: '`from` must not be after `to`', path: ['from'] };

/** `GET /reports/:key` — the figures, one page of them. */
export const reportQuerySchema = defineListQuery({
  sortFields: ['label', 'value'],
  defaultSort: { sort: 'label', order: 'asc' },
  filters: reportFilters,
}).refine(hasOrderedPeriod, periodIssue);
export type ReportQuery = z.infer<typeof reportQuerySchema>;

/** `GET /reports/:key/records` — the records behind the figures, under the same filters. */
export const reportRecordsQuerySchema = defineListQuery({
  sortFields: ['date', 'amount'],
  defaultSort: { sort: 'date', order: 'desc' },
  filters: reportFilters,
}).refine(hasOrderedPeriod, periodIssue);
export type ReportRecordsQuery = z.infer<typeof reportRecordsQuerySchema>;
